
import React from 'react';
import { Button } from '@/components/ui/button';
import { useRetentionOperations } from '@/hooks/useRetentionOperations';
import { useAdminNotifications } from '@/hooks/useAdminNotifications';
import { FileCog, AlertTriangle, CheckCircle2 } from 'lucide-react';

interface RetentionActionsProps {
  state?: string;
  courseType?: string;
  retentionDate: string;
  onDryRunCompleted?: (results: any) => void;
  onExecutionCompleted?: (results: any) => void;
  onVerificationCompleted?: (results: any) => void;
}

export function RetentionActions({
  state,
  courseType,
  retentionDate,
  onDryRunCompleted,
  onExecutionCompleted,
  onVerificationCompleted
}: RetentionActionsProps) {
  const { 
    dryRunMutation, 
    executeMutation, 
    verifyMutation 
  } = useRetentionOperations();

  const { 
    sendRetentionNotification,
    sendUrgentNotification
  } = useAdminNotifications();

  const handleDryRun = async () => {
    try {
      const data = await dryRunMutation.mutateAsync({
        state,
        courseType,
        retentionDate
      });
      
      if (onDryRunCompleted) {
        onDryRunCompleted(data);
      }

      // Send notification about the dry run
      sendRetentionNotification(
        'Retention Dry Run Completed', 
        `A dry run of certificate retention found ${data.certificates_count || 0} certificates that would be archived.`,
        {
          state,
          courseType,
          retentionDate,
          certificatesCount: data.certificates_count || 0
        }
      );
    } catch (error: any) {
      // Send notification about the failure
      sendUrgentNotification(
        'Retention Dry Run Failed',
        `The retention dry run operation failed: ${error.message}`,
        {
          state,
          courseType,
          retentionDate,
          error: error.message
        }
      );
    }
  };

  const handleExecute = async () => {
    try {
      const data = await executeMutation.mutateAsync({
        state,
        courseType,
        retentionDate
      });

      if (onExecutionCompleted) {
        onExecutionCompleted(data);
      }

      // Send notification about the successful execution
      sendRetentionNotification(
        'Retention Process Executed',
        `Certificate retention process successfully archived ${data.archived_count || 0} certificates.`,
        {
          state,
          courseType,
          retentionDate,
          archivedCount: data.archived_count || 0
        }
      );
    } catch (error: any) {
      // Send urgent notification about the failed execution
      sendUrgentNotification(
        'Retention Process Failed',
        `The retention execution operation failed: ${error.message}`,
        {
          state,
          courseType,
          retentionDate,
          error: error.message
        }
      );
    }
  };

  const handleVerify = async () => {
    try {
      const data = await verifyMutation.mutateAsync({
        state,
        courseType
      });

      if (onVerificationCompleted) {
        onVerificationCompleted(data);
      }

      const issuesFound = data.issues_found || 0;
      
      if (issuesFound > 0) {
        // Send notification about compliance issues
        sendUrgentNotification(
          'Retention Compliance Issues Found',
          `Found ${issuesFound} compliance issues with certificate retention. Review needed.`,
          {
            state,
            courseType,
            issuesCount: issuesFound
          }
        );
      } else {
        // Send notification about successful verification
        sendRetentionNotification(
          'Retention Compliance Verified',
          'Certificate retention policy compliance verification completed with no issues found.',
          {
            state,
            courseType
          }
        );
      }
    } catch (error: any) {
      // Send notification about verification failure
      sendUrgentNotification(
        'Retention Verification Failed',
        `The retention verification operation failed: ${error.message}`,
        {
          state,
          courseType,
          error: error.message
        }
      );
    }
  };

  return (
    <div className="flex flex-col gap-4 sm:flex-row">
      <Button
        variant="outline"
        onClick={handleDryRun}
        disabled={dryRunMutation.isPending}
        className="flex items-center gap-2"
      >
        <FileCog className="h-4 w-4" />
        {dryRunMutation.isPending ? 'Running...' : 'Dry Run'}
      </Button>

      <Button
        variant="default"
        onClick={handleExecute}
        disabled={executeMutation.isPending}
        className="flex items-center gap-2"
      >
        <CheckCircle2 className="h-4 w-4" />
        {executeMutation.isPending ? 'Processing...' : 'Execute Retention'}
      </Button>

      <Button
        variant="outline"
        onClick={handleVerify}
        disabled={verifyMutation.isPending}
        className="flex items-center gap-2"
      >
        <AlertTriangle className="h-4 w-4" />
        {verifyMutation.isPending ? 'Verifying...' : 'Verify Compliance'}
      </Button>
    </div>
  );
}
